/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useContext, useState } from 'react';
import './myDoocumentTabs.css';
import '../Form/Button/pill-button.scss';
import Select from '../Form/Select/Select';
import Button from '../Form/Button/Button';
import Loading from '../Loading/Loading';
import Modal from '../Modal/Modal';
import errorImg from '../../assets/images/error.png';
import DownloadIcon from '../../assets/images/download-icon.svg?react';
import ViewIcon from '../../assets/images/view-document.svg?react';
import { MONTHS } from '../../utils/helpers';
import { customerDataContext } from '../../contexts/CustomerDataProvider';
import { useUnifiedAnalytics } from '../../contexts/AnalyticsWrapper';
import { useLogger } from '../../contexts/Logger';

/** @import {Tab, TabsProps} from './TabsTypes' */
/** @import {Account} from '../../contexts/CustomerDataProviderTypes' */
/** @import {Document} from './MyDocumentsTypes' */

function formatDate(/** @type {string} */ dateString) {
    var date = new Date(dateString);
    return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

function MyDoocumentTabs(/** @type {TabsProps} */ { tabs, selected, onSelect }) {
    const selectedTab = tabs.find(tab => tab.key === selected) || tabs[0];
    const customerData = useContext(customerDataContext);
    const analytics = useUnifiedAnalytics();
    const logger = useLogger();
    const [selectedAccount, setSelectedAccount] = useState(/** @type {Account | null} */ (null));
    const [viewLoadingOpen, setViewLoadingOpen] = useState(false);
    const [viewErrorMessage, setViewErrorMessage] = useState('');

    if (!selectedTab)
        return null;

    const setSelectedAccountState = (/** @type {string} */ accountNumber) => {
        const accounts = customerData?.data?.customer.accounts;
        const account = accounts?.find(a => a.accountNumber === accountNumber);
        if (!account) return;
        setSelectedAccount(account);
    };

    const mapAccountDropDown = (/** @type {Account[]} */ accounts) => {
        return accounts.map(account => ({
            label: `${account.product.name} - ${account.accountNumber}`,
            value: account.accountNumber
        }));
    };

    // Helper for analytics tracking for document actions
    /**
     * Track a document action click
     * @param {string} action
     * @param {{documentName?: string, accountNumber?: string}|null|undefined} doc
     */
    const trackDocumentClick = (action, doc) => {
        try {
            const flowName = `${action} ${doc?.documentName || 'document'}`;
            analytics.trackAnalyticsClickEvent(
                flowName,
                window.location.href,
                'my-documents',
                '',
                '',
                { accountNumber: doc?.accountNumber },
                'button click',
                `my_documents|button_click|${action.replace(/\s+/g, '_')}`,
                'my_documents'
            );
        } catch (err) {
            logger && logger.error && logger.error('Failed to track document click', { err });
        }
    };

    const renderAdhocStatements = (/** @type {Tab} */ tab) => {
        if (!customerData?.data) return null;
        const accounts = customerData.data.customer.accounts;
        const activeAccounts = accounts.filter(e => e.accountStatus !== 'Closed');
        if (activeAccounts.length === 0) return null;

        if (activeAccounts.length === 1) {
            return (
                <div className="my-doocument-tabs__adhoc">
                    <h3 className="my-doocument-tabs__section-heading">Get your current statement</h3>
                    <p className="my-doocument-tabs__section-description">Select an account. Then select &apos;Download  your statement&apos; to get your most recent statement.</p>
                    <div className="my-doocument-tabs__adhoc-controls">
                        <div className="my-doocument-tabs__adhoc-account">
                            {activeAccounts[0].product.name} - {activeAccounts[0].accountNumber}
                        </div>
                        <Button
                            label="Download your statement"
                            leftIcon={<DownloadIcon aria-hidden="true" />}
                            size="med"
                            variant="pill"
                            
                            onClick={(/** @type {any} */ e) => {
                                // track analytics and then invoke the configured action
                                trackDocumentClick('download', { documentName: 'current statement', accountNumber: activeAccounts[0].accountNumber });
                                if (tab.buttonAction) {
                                    try { 
                                        // tab.buttonAction returns an event handler (e) => {}
                                        const handler = tab.buttonAction(activeAccounts[0]);
                                        handler && handler(e);
                                    } catch (ex) { /* swallow */ }
                                }
                            }}
                        />
                    </div>
                </div>
            );
        }

        const selectorAccounts = mapAccountDropDown(activeAccounts);
        return (
            <div className="my-doocument-tabs__adhoc">
                <h3 className="my-doocument-tabs__section-heading">Get your current statement</h3>
                <p className="my-doocument-tabs__section-description">Select an account. Then select &apos;Download  your statement&apos; to get your most recent statement.</p>
                <div className="my-doocument-tabs__adhoc-controls">
                    <div className="my-doocument-tabs__adhoc-selector">
                        <Select
                            name="account"
                            placeholder="Select an account"
                            options={selectorAccounts}
                            onChange={option => option && setSelectedAccountState(option.value)}
                            disabled={false}
                        />
                    </div>
                    <Button
                        label="Download your statement"
                        leftIcon={<DownloadIcon aria-hidden="true" />}
                        size="med"
                        variant="pill"
                        disabled={!selectedAccount}
                        
                        onClick={(/** @type {any} */ e) => {
                            trackDocumentClick('download', { documentName: 'current statement', accountNumber: selectedAccount?.accountNumber });
                            if (tab.buttonAction && selectedAccount) {
                                try { 
                                    const handler = tab.buttonAction(selectedAccount);
                                    handler && handler(e);
                                } catch (ex) { /* swallow */ }
                            }
                        }}
                    />
                </div>
            </div>
        );
    };

    const renderAction = (/** @type {Document} */ doc) => {
        if (doc.downloadAction) {
            return (
                <Button
                    label="Download"
                    leftIcon={<DownloadIcon aria-hidden="true" />}
                    size="med"
                    variant="pill"
                    onClick={(/** @type {any} */ e) => { 
                        trackDocumentClick('download', doc); 
                        try { doc.downloadAction?.(e); } catch (ex) { /* swallow */ } 
                    }}
                />
            );
        }

        return (
            <Button
                ariaLabel={`Download ${doc.documentName || 'document'}`}
                href={doc.downloadLink}
                label="Download"
                leftIcon={<DownloadIcon aria-hidden="true" />}
                rel="noreferrer"
                size="med"
                target="_blank"
                onClick={() => trackDocumentClick('download', doc)}
                variant="pill"
            />
        );
    };

    const handleViewClick = async (/** @type {Document} */ doc, /** @type {any} */ e) => {
        setViewErrorMessage('');
        setViewLoadingOpen(true);
        trackDocumentClick('view', doc);

        try {
            if (doc.viewAction) {
                await doc.viewAction(e);
            } else {
                e && e.preventDefault && e.preventDefault();
                const viewUrl = doc.downloadLink?.includes('?')
                    ? `${doc.downloadLink}&inline=true`
                    : `${doc.downloadLink}#view=FitH`;
                window.open(viewUrl, '_blank', 'noopener,noreferrer');
            }
        } catch (err) {
            const message = err instanceof Error && err.message
                ? err.message
                : 'Sorry, we couldn’t load your document.';
            setViewErrorMessage(message);
            logger && logger.error && logger.error('Failed to view document', { err, documentName: doc.documentName });
        } finally {
            setViewLoadingOpen(false);
        }
    };

    const renderViewAction = (/** @type {Document} */ doc) => (
        <Button
            ariaLabel={`View ${doc.documentName || 'document'}`}
            label="View"
            leftIcon={<ViewIcon aria-hidden="true" />}
            onClick={(/** @type {any} */ e) => handleViewClick(doc, e)}
            size="med"
            variant="pill-outline"
        />
    );

    const renderViewErrorModal = () => (
        <Modal onClose={() => setViewErrorMessage('')}>
            <img src={errorImg} className='my-documents__image' alt="" />
            <h4 className="my-documents__title">Please try again</h4>
            <p className="my-documents__slim">Sorry, we couldn’t load your document.</p>
        </Modal>
    );

    const renderDocumentList = (/** @type {Document[]} */ documents, /** @type {{ showView?: boolean }} */ options = {}) => {
        const showView = options.showView !== false;

        return (
            <div className="my-doocument-tabs__document-list">
                <div className="my-doocument-tabs__list-header">
                    <span className="my-doocument-tabs__header-cell my-doocument-tabs__header-cell--date">
                        <span className="my-doocument-tabs__header-date-desktop">Date</span>
                        <span className="my-doocument-tabs__header-date-mobile">Date &amp; Title</span>
                    </span>
                    <span className="my-doocument-tabs__header-cell my-doocument-tabs__header-cell--title">Title</span>
                    <span className="my-doocument-tabs__header-cell my-doocument-tabs__header-cell--account">Account number</span>
                    <span className="my-doocument-tabs__header-cell my-doocument-tabs__header-cell--actions">&nbsp;</span>
                </div>
                {documents.map((document, index) => (
                    <div
                        key={`${document.documentName}-${document.accountNumber}-${index}`}
                        className="my-doocument-tabs__document-row"
                    >
                        <span className="my-doocument-tabs__cell my-doocument-tabs__cell--date">{formatDate(document.createdDate)}</span>
                        <span className="my-doocument-tabs__cell my-doocument-tabs__cell--title">{document.documentName}</span>
                        <span className="my-doocument-tabs__cell my-doocument-tabs__cell--account">{document.accountNumber}</span>
                        <span className="my-doocument-tabs__cell my-doocument-tabs__cell--actions">
                            {showView && renderViewAction(document)}
                            {renderAction(document)}
                        </span>
                    </div>
                ))}
            </div>
        );
    };

    const renderStatementsContent = (/** @type {Tab} */ tab) => {
        return (
            <>
                {renderAdhocStatements(tab)}

                <div className="my-doocument-tabs__history">
                    <h3 className="my-doocument-tabs__section-heading">Statement history</h3>
                    <p className="my-doocument-tabs__section-description">View and download previous statements for all of your accounts.</p>

                    {tab.content.length === 0 ? (
                        <p className="my-doocument-tabs__empty">{tab.noContent}</p>
                    ) : renderDocumentList(tab.content, { showView: true })}
                </div>
            </>
        );
    };

    const renderDefaultContent = (/** @type {Tab} */ tab) => {
        if (tab.content.length === 0) {
            return <p className="my-doocument-tabs__empty">{tab.noContent}</p>;
        }

        return renderDocumentList(tab.content, { showView: true });
    };

    return (
        <div className="my-doocument-tabs">
            {viewErrorMessage && renderViewErrorModal()}
            {viewLoadingOpen && (
                <div className="my-doocument-tabs__view-loading" role="status" aria-live="polite" aria-label="Loading document">
                    <Loading message="Loading document..." />
                </div>
            )}
            <div className="my-doocument-tabs__list" role="tablist" aria-label="Document categories">
                {tabs.map(tab => {
                    const isSelected = selectedTab.key === tab.key;

                    return (
                        <button
                            key={tab.key}
                            type="button"
                            className={`my-doocument-tabs__tab${isSelected ? ' my-doocument-tabs__tab--selected' : ''}`}
                            onClick={() => {
                                onSelect(tab.key);
                                setSelectedAccount(null);
                            }}
                            role="tab"
                            aria-selected={isSelected}
                        >
                            {tab.name}
                        </button>
                    );
                })}
            </div>

            <div className="my-doocument-tabs__panel">
                {selectedTab.key === 'statements'
                    ? renderStatementsContent(selectedTab)
                    : renderDefaultContent(selectedTab)
                }
            </div>
        </div>
    );
}

export default MyDoocumentTabs;
